import React, { useState, useRef, useEffect } from 'react';
import { FaBell } from 'react-icons/fa';
import styled from 'styled-components';
import moment from 'moment';

const NotificationContainer = styled.div`
  position: relative;
  display: inline-block;
`;

const IconWrapper = styled.div`
  position: relative;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  
  .icon {
    font-size: 24px;
    color: #333;
  }
  
  .count {
    position: absolute;
    top: -8px;
    right: -8px;
    background-color: red;
    color: white;
    border-radius: 50%;
    width: 20px;
    height: 20px;
    display: ${props => props.count > 0 ? 'flex' : 'none'};
    align-items: center;
    justify-content: center;
    font-size: 12px;
    font-weight: bold;
  }
`;

const Dropdown = styled.div`
  position: absolute;
  top: 100%;
  right: 0;
  margin-top: 8px;
  width: 380px;
  max-height: 450px;
  background: white;
  border-radius: 6px;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.15);
  z-index: 1000;
  display: ${props => props.isOpen ? 'block' : 'none'};
  overflow: hidden;
`;

const Header = styled.div`
  background-color: #f5f5f5;
  color: #000;
  padding: 10px 15px;
  display: flex;
  justify-content: space-between;
  align-items: center;
  border-bottom: 1px solid #e0e0e0;
  
  .header-title {
    font-weight: bold;
    font-size: 14px;
    color: #000;
  }
  
  .header-option {
    font-size: 12px;
    cursor: pointer;
    text-decoration: underline;
    color: #000;
    &:hover {
      opacity: 0.7;
    }
  }
`;

const ItemsContainer = styled.div`
  max-height: 350px;
  overflow-y: auto;
`;

const NotificationCard = styled.div`
  padding: 10px 15px;
  border-bottom: 1px solid #e0e0e0;
  cursor: pointer;
  transition: background-color 0.2s;
  background-color: ${props => props.isRead ? '#FFF' : '#ebedf0'};
  
  &:hover {
    background-color: ${props => props.isRead ? '#f5f5f5' : '#e0e0e0'};
  }
  
  &:last-child {
    border-bottom: none;
  }
`;

const CardContent = styled.div`
  display: flex;
  gap: 8px;
  
  .image {
    width: 40px;
    height: 40px;
    border-radius: 50%;
    background-color: #ddd;
    display: none;
  }
  
  .content {
    flex: 1;
    display: flex;
    flex-direction: column;
    
    .message-row {
      display: flex;
      justify-content: space-between;
      align-items: flex-start;
      gap: 10px;
      
      .message {
        flex: 1;
        font-size: 13px;
        color: #333;
        line-height: 1.3;
        text-align: left;
      }
      
      .time {
        font-size: 10px;
        color: #999;
        white-space: nowrap;
        flex-shrink: 0;
        font-weight: normal;
        opacity: 0.8;
      }
    }
  }
`;

const EmptyState = styled.div`
  padding: 30px 15px;
  text-align: center;
  color: #666;
  font-size: 13px;
`;

const NotificationsMenu = ({ 
  data = [], 
  header = { title: "Notifications" },
  markAsRead
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const [notifications, setNotifications] = useState(data);
  const containerRef = useRef(null);

  useEffect(() => {
    setNotifications(data);
  }, [data]);

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (containerRef.current && !containerRef.current.contains(event.target)) {
        setIsOpen(false);
      }
    };

    if (isOpen) {
      document.addEventListener('mousedown', handleClickOutside);
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [isOpen]);

  const unreadCount = notifications.filter(n => !n.isRead).length;

  const handleNotificationClick = (notification) => {
    if (!notification.isRead && markAsRead) {
      markAsRead(notification);
      // Atualiza localmente
      setNotifications(prev => 
        prev.map(n => 
          n.notificationID === notification.notificationID 
            ? { ...n, isRead: true }
            : n
        )
      );
    }
  };

  const handleMarkAllAsRead = () => {
    if (header.option && header.option.onClick) {
      header.option.onClick();
      // Atualiza localmente
      setNotifications(prev => prev.map(n => ({ ...n, isRead: true })));
    }
  };

  return (
    <NotificationContainer ref={containerRef}>
      <IconWrapper 
        count={unreadCount} 
        onClick={() => setIsOpen(!isOpen)}
      >
        <FaBell className="icon" />
        {unreadCount > 0 && (
          <div className="count">{unreadCount > 99 ? '99+' : unreadCount}</div>
        )}
      </IconWrapper>
      
      <Dropdown isOpen={isOpen}>
        <Header>
          <div className="header-title" style={header.style}>
            {header.title}
          </div>
          {header.option && (
            <div 
              className="header-option" 
              onClick={handleMarkAllAsRead}
            >
              {header.option.text}
            </div>
          )}
        </Header>
        
        <ItemsContainer>
          {notifications.length === 0 ? (
            <EmptyState>No notifications</EmptyState>
          ) : (
            notifications.map((notification, index) => (
              <NotificationCard
                key={notification.notificationID || index}
                isRead={notification.isRead}
                onClick={() => handleNotificationClick(notification)}
              >
                <CardContent>
                  {notification.image && (
                    <div 
                      className="image" 
                      style={{ 
                        backgroundImage: `url(${notification.image})`,
                        display: notification.image ? 'block' : 'none'
                      }}
                    />
                  )}
                  <div className="content">
                    <div className="message-row">
                      <div className="message">{notification.message}</div>
                      {notification.receivedTime && (
                        <div className="time">{notification.receivedTime}</div>
                      )}
                    </div>
                  </div>
                </CardContent>
              </NotificationCard>
            ))
          )}
        </ItemsContainer>
      </Dropdown>
    </NotificationContainer>
  );
};

export default NotificationsMenu;

